'use client';

import { useSyncExternalStore } from 'react';

/**
 * Read a sessionStorage value through React's external-store contract instead of
 * reading it in an effect and copying it into state.
 *
 * The effect version is what react-hooks/set-state-in-effect flags, and it is also the
 * less correct shape: sessionStorage is an external system, so React wants a subscription
 * and a snapshot, not a one-shot setState. useSyncExternalStore gives both, and
 * getServerSnapshot keeps the first client render identical to the server HTML — the real
 * value arrives right after hydration, which is the same moment the previous effect ran,
 * so the visible behaviour is unchanged.
 */
const subscribe = (onStoreChange: () => void) => {
  window.addEventListener('storage', onStoreChange);
  return () => window.removeEventListener('storage', onStoreChange);
};

export function useClientStorage(key: string): string | null {
  return useSyncExternalStore(
    subscribe,
    () => sessionStorage.getItem(key),
    () => null,
  );
}

/**
 * True only once the client snapshot is what React is rendering.
 *
 * The comment above says the real value arrives "right after hydration, which is the same moment the
 * previous effect ran". That is true for displaying a value and false for acting on it. During hydration
 * React renders with getServerSnapshot, and the effect from that commit runs with null — before the client
 * value is read. So an effect that redirects when the store is empty fires on every reload for a user who
 * is actually signed in.
 *
 * Measured on 2026-10-14 against the live portal: after a reload of /portal/overview the page redirected to
 * /portal/login even though sessionStorage still held refresh_token, and the three usage endpoints answered
 * 200 before the redirect. The initial login path does not hit this because the callback navigates client
 * side, so the overview page is never hydrated.
 *
 * Guarding the decision with this hook keeps the server HTML matching (the render still shows the loading
 * state) while the redirect only fires when the client genuinely has no session.
 */
export function useIsHydrated(): boolean {
  return useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );
}
